import { useMemo } from 'react'
import { dayKey, densityLevel, meetingsOnDay, monthGrid, sameDay } from '../../lib/calendar'
import type { MeetingView } from '../../types/calendar'
import { MonthChip } from './MeetingChip'

// The month: six weeks of seven, every meeting a chip in its account's colour, the busiest days warmer
// (the density heat), today ringed, the other months' days quiet. A crowded day folds into "+n more",
// which opens that day on the agenda.

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTH_SHORT = new Intl.DateTimeFormat('en-US', { month: 'short' })
const MAX_CHIPS = 3

export function MonthView({ anchor, meetings, now, selectedId, onPick, onOpenDay }: {
  anchor: Date; meetings: MeetingView[]; now: Date; selectedId: string | null; onPick: (m: MeetingView) => void; onOpenDay: (day: Date) => void
}) {
  const grid = useMemo(() => monthGrid(anchor), [anchor])
  const items = useMemo(() => meetings.map((m) => ({ ...m.meeting, view: m })), [meetings])
  const perDay = useMemo(() => new Map(grid.flat().map((d) => [dayKey(d), meetingsOnDay(items, d).map((x) => x.view)])), [grid, items])
  const busiest = Math.max(0, ...[...perDay.values()].map((l) => l.length))

  return (
    <div className="cal-month" role="grid" aria-label="Month">
      <div className="cal-month-head" role="row">
        {WEEKDAYS.map((w) => <div key={w} className={`cal-month-headcell ${w === 'Sat' || w === 'Sun' ? 'weekend' : ''}`} role="columnheader">{w}</div>)}
      </div>
      {grid.map((week, wi) => (
        <div key={wi} className="cal-month-row" role="row">
          {week.map((day) => {
            const list = perDay.get(dayKey(day)) ?? []
            const level = densityLevel(list.length, busiest)
            const weekend = day.getDay() === 0 || day.getDay() === 6
            const cls = ['cal-cell', `heat-${level}`, day.getMonth() !== anchor.getMonth() ? 'other' : '', sameDay(day, now) ? 'today' : '', weekend ? 'weekend' : ''].filter(Boolean).join(' ')
            return (
              <div key={dayKey(day)} className={cls} role="gridcell" aria-label={`${dayKey(day)}, ${list.length} meeting${list.length === 1 ? '' : 's'}`}>
                <div className="cal-cell-head">
                  <button className="cal-daynum" onClick={() => onOpenDay(day)} title="Open this day on the agenda">
                    {day.getDate() === 1 ? `${MONTH_SHORT.format(day)} ` : ''}{day.getDate()}
                  </button>
                  {list.length > 0 && <span className="cal-cell-count">{list.length}</span>}
                </div>
                <div className="cal-chips">
                  {list.slice(0, MAX_CHIPS).map((m) => <MonthChip key={m.meeting.meetingId} m={m} now={now} selected={m.meeting.meetingId === selectedId} onPick={onPick} />)}
                  {list.length > MAX_CHIPS && <button className="cal-more" onClick={() => onOpenDay(day)}>+{list.length - MAX_CHIPS} more</button>}
                </div>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}
